/**
 * Authority Offline Complaints View
 * Dedicated route component for Authority offline complaints management
 */

import React, { useState, useRef, useEffect } from 'react';
import OfflineComplaintsView from './OfflineComplaintsView';
import { OfflineComplaintDialog } from '../../components/dialogs';
import { ViewComplaintDialog } from '../../components/dialogs';
import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/tableExport';
import complaintService from '../../services/complaintService';
import Toast from '../../components/Toast';

const AuthorityOfflineComplaintsView = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [offlineComplaints, setOfflineComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success',
  });
  const hasFetchedRef = useRef(false);
  const dropdownRef = useRef(null);
  const downloadDropdownRef = useRef(null);

  // Normalize offline complaint data
  const normalizeOfflineComplaint = (item) => {
    if (!item || typeof item !== 'object') return null;

    // Format date consistently
    let formattedDate = '';
    if (item.created_at) {
      try {
        const date = new Date(item.created_at);
        formattedDate = date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch (e) {
        formattedDate = item.created_at;
      }
    } else if (item.date) {
      formattedDate = item.date;
    }

    return {
      ...item,
      complaint_id: item.complaint_id || item.id || '',
      date: formattedDate,
      created_at: item.created_at || '',
      name: item.name || item.user?.name || '',
      mobile: item.mobile || item.user?.mobile || '',
      email: item.email || item.user?.email || '',
      address: item.address || '',
      complaint: item.your_complaint || item.complaint || item.description || '',
      your_complaint: item.your_complaint || item.complaint || item.description || '',
      files: Array.isArray(item.files) ? item.files : [],
      documents: Array.isArray(item.files) ? item.files : (item.documents || []),
      // Preserve user object
      user: item.user || null,
    };
  };

  // Fetch offline complaints from API
  const fetchOfflineComplaints = async () => {
    if (loading) return; // Prevent multiple simultaneous calls
    
    setLoading(true);
    setError(null);
    try {
      const response = await complaintService.getAuthorityOfflineComplaints();
      if (response.success) {
        const payload = response.data || {};
        const list = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
        console.log('Raw API response list:', list);
        const normalizedList = list.map(normalizeOfflineComplaint).filter(Boolean);
        console.log('Normalized complaints:', normalizedList);
        setOfflineComplaints(normalizedList);
        hasFetchedRef.current = true;
      } else {
        setError(response.message || 'Failed to fetch offline complaints');
        setToast({
          isVisible: true,
          message: response.message || 'Failed to fetch offline complaints',
          type: 'error',
        });
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch offline complaints';
      setError(errorMessage);
      setToast({
        isVisible: true,
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    if (!hasFetchedRef.current && !loading) {
      fetchOfflineComplaints();
    }
  }, []);

  // Handler functions
  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleSaveComplaint = async (formData) => {
    // The dialog already handles the API call, we just need to refresh the list
    setIsDialogOpen(false);
    // Refresh the list
    hasFetchedRef.current = false;
    await fetchOfflineComplaints();
  };

  const handleComplaintView = (complaint) => {
    setSelectedComplaint(complaint);
  };

  const handleCloseView = () => {
    setSelectedComplaint(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get columns for export
  const getColumns = () => [
    { field: 'date', headerName: 'Date' },
    { field: 'complaint_id', headerName: 'Complaint ID' },
    { field: 'name', headerName: 'Complainant Name' },
    { field: 'mobile', headerName: 'Mobile' },
    { field: 'complaint', headerName: 'Complaint Description' },
  ];

  // Get filtered data for export
  const getFilteredData = () => {
    if (!searchTerm) return offlineComplaints;
    
    return offlineComplaints.filter(complaint =>
      complaint.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.complaint_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.complaint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleExportCSV = () => {
    try {
      const filteredData = getFilteredData();
      exportToCSV(filteredData, getColumns(), 'offline-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredData();
      exportToExcel(filteredData, getColumns(), 'offline-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredData();
      exportToPDF(filteredData, getColumns(), 'offline-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };


  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header with Search (Left) and Buttons (Right) */}
      <div className="flex justify-between items-center pb-4 flex-shrink-0 gap-4">
        {/* Search Bar - Left Aligned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-96">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-gray-700"
              placeholder="Search by complaint ID, name, or description..."
            />
          </div>
        </div>

        {/* Buttons - Right Aligned */}
        <div className="flex items-center gap-3">
          {/* Download Button with Dropdown */}
          <div className="relative" ref={downloadDropdownRef}>
            <button
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
              <svg className={`w-4 h-4 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Download Dropdown Menu */}
            {showDownloadDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download CSV</span>
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download Excel</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Create Offline Complaint Button */}
          <button
            onClick={handleOpenDialog}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Offline Complaint</span>
          </button>
        </div>
      </div>

      {/* Complaints Table */}
      <div className="flex-1 min-h-0">
        {loading && offlineComplaints.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading offline complaints...</div>
          </div>
        ) : error && offlineComplaints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-red-500 mb-4">{error}</div>
            <button
              onClick={fetchOfflineComplaints}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <OfflineComplaintsView
            complaints={offlineComplaints}
            searchTerm={searchTerm}
            dateFilter={null}
            onComplaintView={handleComplaintView}
          />
        )}
      </div>

      {/* Create Offline Complaint Dialog */}
      <OfflineComplaintDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSaveComplaint}
      />

      {/* View Complaint Dialog */}
      <ViewComplaintDialog
        isOpen={!!selectedComplaint}
        complaint={selectedComplaint}
        onClose={handleCloseView}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default AuthorityOfflineComplaintsView;

